import { CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProductsService } from '../../../data-access/src/lib/products.service';
import {
  ConfiguracionRolService,
  guardarContextoDeProducto,
  leerContextoDeProducto,
} from '@nebula-shared/data-access';

/**
 * La ruta de un producto sin barras ni el prefijo `layout/`, como la guarda el selector
 * (`products.component`) en `selectedRoute`.
 *
 * @param ruta ruta del producto tal como llega del menu.
 * @returns la ruta limpia.
 */
export function rutaLimpiaDeProducto(ruta: string | null | undefined): string {
  return String(ruta ?? '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/^layout\//, '');
}

/**
 * El producto de la pestaña es el de la URL que se abre.
 *
 * <p>Una pestaña nueva hereda el ultimo producto elegido en otra (Mantis #455). Si se abre en ella
 * el enlace de otro producto, el menu, el contenido y los parametros se quedaban en el heredado y la
 * pantalla salia vacia. Cuando la ruta del producto no es la del contexto de la pestaña, el guard
 * busca ese producto entre los del usuario y lo fija como lo hace el selector: id, codigo y ruta.
 * Si la ruta ya es la del contexto no consulta nada; si el usuario no tiene el producto o la
 * consulta falla, deja pasar sin cambiar el contexto.</p>
 *
 * @param route la ruta del producto (`accounting`, `tesorería`, ...).
 * @returns `true` siempre: el guard solo alinea el contexto.
 */
export const productoDeLaRutaGuard: CanActivateFn = (route) => {
  const rutaDeLaUrl = rutaLimpiaDeProducto(route.routeConfig?.path);
  if (!rutaDeLaUrl || leerContextoDeProducto('selectedRoute') === rutaDeLaUrl) {
    return true;
  }

  const productsService = inject(ProductsService);
  const configuracionRol = inject(ConfiguracionRolService);

  return productsService.getModules().pipe(
    map((productos) => {
      const producto = productos.find((p) => rutaLimpiaDeProducto(p.route) === rutaDeLaUrl);
      if (producto) {
        guardarContextoDeProducto('selectedProductId', String(producto.id));
        configuracionRol.fijarProductoActivo(producto.code);
        guardarContextoDeProducto('selectedRoute', rutaDeLaUrl);
      }
      return true;
    }),
    catchError(() => of(true)),
  );
};
